// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import * as ProductsAPI from 'lib/api/products';
import * as CommentsAPI from 'lib/api/products/comments';
import * as LikesAPI from 'lib/api/products/like';
import { applyPenders, type ResponseAction } from 'lib/common';

export type TocItem = {
  anchor: string,
  level: number,
  text: string,
};

/* ACTION TYPE */
const READ_PRODUCT = 'products/READ_PRODUCT';
const UNLOAD_PRODUCT = 'products/UNLOAD_PRODUCT';
const SET_TOC = 'products/SET_TOC';
const ACTIVATE_HEADING = 'products/ACTIVATE_HEADING';
const WRITE_COMMENT = 'products/WRITE_COMMENT';
const READ_COMMENTS = 'products/READ_COMMENTS';
const READ_SUBCOMMENTS = 'products/READ_SUBCOMMENTS';
const LIKE = 'products/LIKE';
const UNLIKE = 'products/UNLIKE';
const GET_LIKES_COUNT = 'products/GET_LIKES_COUNT';
const TOGGLE_ASK_REMOVE = 'products/TOGGLE_ASK_REMOVE';
const OPEN_COMMENT_REMOVE = 'products/OPEN_COMMENT_REMOVE';
const CANCEL_COMMENT_REMOVE = 'products/CANCEL_COMMENT_REMOVE';
const REMOVE_COMMENT = 'products/REMOVE_COMMENT';
const EDIT_COMMENT = 'products/EDIT_COMMENT';
const GET_SEQUENCES = 'products/GET_SEQUENCES';

type OpenCommentRemovePayload = { commentId: string, parentId: ?string };

export interface ProductsActionCreators {
  readProduct(payload: ProductsAPI.ReadProductPayload): any;

  unloadProduct(): any;

  setToc(payload: ?(TocItem[])): any;

  activateHeading(payload: string): any;

  writeComment(payload: CommentsAPI.WriteCommentPayload): any;

  readComments(payload: CommentsAPI.ReadCommentsPayload): any;

  readSubComments(payload: CommentsAPI.ReadSubCommentsPayload): any;

  like(productId: string): any;

  unlike(productId: string): any;

  getLikesCount(productId: string): any;

  toggleAskRemove(): any;

  openCommentRemove(payload: OpenCommentRemovePayload): any;

  cancelCommentRemove(): any;

  removeComment(payload: CommentsAPI.RemoveCommentPayload): any;

  editComment(payload: CommentsAPI.EditCommentPayload): any;

  getSequences(productId: string): any;
}

export const actionCreators = {
  readProduct: createAction(READ_PRODUCT, ProductsAPI.readProduct),
  unloadProduct: createAction(UNLOAD_PRODUCT),
  setToc: createAction(SET_TOC, (toc: ?(TocItem[])) => toc),
  activateHeading: createAction(ACTIVATE_HEADING, (headingId: string) => headingId),
  writeComment: createAction(WRITE_COMMENT, CommentsAPI.writeComment),
  readComments: createAction(READ_COMMENTS, CommentsAPI.readComments),
  readSubComments: createAction(READ_SUBCOMMENTS, CommentsAPI.readSubComments, meta => meta),
  like: createAction(LIKE, LikesAPI.like),
  unlike: createAction(UNLIKE, LikesAPI.unlike),
  getLikesCount: createAction(GET_LIKES_COUNT, LikesAPI.getLikesCount),
  toggleAskRemove: createAction(TOGGLE_ASK_REMOVE),
  openCommentRemove: createAction(
    OPEN_COMMENT_REMOVE,
    (payload: OpenCommentRemovePayload) => payload,
  ),
  cancelCommentRemove: createAction(CANCEL_COMMENT_REMOVE),
  removeComment: createAction(REMOVE_COMMENT, CommentsAPI.removeComment),
  editComment: createAction(EDIT_COMMENT, CommentsAPI.editComment, payload => payload),
  getSequences: createAction(GET_SEQUENCES, ProductsAPI.getProductSequences),
};

type SetTocAction = ActionType<typeof actionCreators.setToc>;
type ActivateHeadingAction = ActionType<typeof actionCreators.activateHeading>;
type OpenCommentRemoveAction = ActionType<typeof actionCreators.openCommentRemove>;
export type Categories = { id: string, name: string, url_slug: string }[];
export type ProductPrice = {
  id: string,
  fk_product_id: string,
  type: string,
  status: string,
  description: string,
  part_number: string,
  cas_number: string,
  unit: string,
  price: number,
}[];
export type Comment = {
  id: string,
  text: ?string,
  reply_to: string,
  level: number,
  created_at: string,
  updated_at: string,
  user: {
    username: ?string,
    thumbnail: ?string,
  },
  replies_count: number,
};
export type ProductData = {
  id: string,
  title: string,
  body: string,
  thumbnail: ?string,
  is_markdown: boolean,
  is_private: boolean,
  released_at: string,
  created_at: string,
  updated_at: string,
  tags: string[],
  badges: string[],
  categories: Categories,
  url_slug: string,
  likes: number,
  liked: boolean,
  comments_count: 0,
  product_price: ProductPrice,
  user: {
    username: string,
    id: string,
    thumbnail: ?string,
    short_bio: ?string,
    display_name: ?string,
  },
  meta: {
    code_theme: string,
    short_description: ?string,
  },
  series: ?{
    description: string,
    id: string,
    index: number,
    length: number,
    url_slug: string,
    thumbnail: ?string,
    name: string,
    list: { index: number, id: string, title: string, url_slug: string }[],
  },
};

export type SubCommentsMap = {
  [string]: Comment[],
};

export type RemoveComment = {
  visible: boolean,
  commentId: ?string,
  parentId: ?string,
};

export type ProductSequence = {
  id: string,
  title: string,
  body: string,
  meta: ?{
    code_theme: string,
    short_description: string,
  },
  url_slug: string,
  created_at: string,
  released_at: string,
  thumbnail: string,
};

export type Products = {
  product: ?ProductData,
  toc: ?(TocItem[]),
  activeHeading: ?string,
  comments: ?(Comment[]),
  sequences: ?(ProductSequence[]),
  subCommentsMap: SubCommentsMap,
  askRemove: boolean,
  removeComment: RemoveComment,
};

const initialState: Products = {
  product: null,
  toc: null,
  activeHeading: null,
  comments: null,
  sequences: [],
  subCommentsMap: {},
  askRemove: false,
  removeComment: {
    commentId: null,
    parentId: null,
    visible: false,
  },
};

const reducer = handleActions(
  {
    [UNLOAD_PRODUCT]: (state, action) => {
      return initialState;
    },
    [SET_TOC]: (state, action: SetTocAction) => {
      return produce(state, draft => {
        draft.toc = action.payload;
      });
    },
    [ACTIVATE_HEADING]: (state, action: ActivateHeadingAction) => {
      return produce(state, draft => {
        draft.activeHeading = action.payload;
      });
    },
    [TOGGLE_ASK_REMOVE]: state => {
      return {
        ...state,
        askRemove: !state.askRemove,
      };
    },
    [OPEN_COMMENT_REMOVE]: (state, { payload }: OpenCommentRemoveAction) => {
      return {
        ...state,
        removeComment: {
          ...payload,
          visible: true,
        },
      };
    },
    [CANCEL_COMMENT_REMOVE]: state => {
      return {
        ...state,
        removeComment: initialState.removeComment,
      };
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: READ_PRODUCT,
    onSuccess: (state: Products, action: ResponseAction) => {
      return produce(state, draft => {
        if (!action.payload) return;
        draft.product = action.payload.data;
      });
    },
  },
  {
    type: WRITE_COMMENT,
    onSuccess: (state: Products, action: ResponseAction) => {
      return produce(state, draft => {
        if (draft.comments) {
          // draft.comments.push(action.payload.data);
          return;
        }
        draft.comments = [action.payload.data];
      });
    },
  },
  {
    type: READ_COMMENTS,
    onSuccess: (state: Products, action: ResponseAction) => {
      return {
        ...state,
        comments: action.payload.data,
      };
    },
  },
  {
    type: READ_SUBCOMMENTS,
    onSuccess: (state: Products, action: ResponseAction) => {
      return produce(state, draft => {
        const { parentId, commentId } = action.meta;
        // is root comment
        if (!draft.comments) return;
        let comment = null;
        if (!parentId) {
          comment = draft.comments.find(c => c.id === commentId);
        } else {
          if (!draft.subCommentsMap[parentId]) return;
          comment = draft.subCommentsMap[parentId].find(c => c.id === commentId);
        }
        if (!comment) return;
        comment.replies_count = action.payload.data.length;
        draft.subCommentsMap[action.meta.commentId] = action.payload.data;
      });
    },
  },
  {
    type: LIKE,
    onPending: (state: Products) => {
      return produce(state, draft => {
        if (draft.product) {
          draft.product.likes += 1;
          draft.product.liked = true;
        }
      });
    },
    onSuccess: (state: Products, action: ResponseAction) => {
      return produce(state, draft => {
        if (draft.product) {
          draft.product.likes = action.payload.data && action.payload.data.likes;
          draft.product.liked = true;
        }
      });
    },
    onFailure: (state: Products) => {
      return produce(state, draft => {
        if (draft.product) {
          draft.product.likes -= 1;
          draft.product.liked = false;
        }
      });
    },
  },
  {
    type: UNLIKE,
    onPending: (state: Products) => {
      return produce(state, draft => {
        if (draft.product) {
          draft.product.likes -= 1;
          draft.product.liked = false;
        }
      });
    },
    onSuccess: (state: Products, action: ResponseAction) => {
      return produce(state, draft => {
        if (draft.product) {
          draft.product.likes = action.payload.data && action.payload.data.likes;
          draft.product.liked = false;
        }
      });
    },
    onFailure: (state: Products) => {
      return produce(state, draft => {
        if (draft.product) {
          draft.product.likes += 1;
          draft.product.liked = true;
        }
      });
    },
  },
  {
    type: REMOVE_COMMENT,
    onPending: (state: Products) => {
      return produce(state, draft => {
        draft.removeComment.visible = false;
      });
    },
    onSuccess: (state: Products) => {
      const { commentId, parentId } = state.removeComment;
      return produce(state, draft => {
        const target = (() => {
          const { subCommentsMap, comments } = draft;
          // Case 1. it is subComment
          if (parentId) {
            const subComments = subCommentsMap[parentId];
            if (!subComments) return null;
            const comment = subComments.find(c => c.id === commentId);
            return comment;
          }
          // Case 2. it is normal comment
          if (!comments) return null;
          const comment = comments.find(c => c.id === commentId);
          return comment;
        })();

        if (!target) return; // ensure target is found
        target.text = null;
        target.user.username = null;
        target.user.thumbnail = null;
      });
    },
  },
  {
    type: EDIT_COMMENT,
    onSuccess: (state: Products, action: ResponseAction) => {
      const { payload, meta } = action;
      const { commentId, parentId } = meta;
      return produce(state, draft => {
        const { subCommentsMap, comments } = draft;
        // is subComment
        if (parentId) {
          const subComments = subCommentsMap[parentId];
          if (!subComments) return;
          const index = subComments.findIndex(c => c.id === commentId);
          subComments[index] = payload.data;
          return;
        }
        // is root comment
        if (!comments) return;
        const index = comments.findIndex(c => c.id === commentId);
        comments[index] = payload.data;
      });
    },
  },
  {
    type: GET_SEQUENCES,
    onSuccess: (state: Products, action: ResponseAction) => {
      return {
        ...state,
        sequences: action.payload.data,
      };
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/products.js